import { useCallback, useRef, useState } from "react";

/**
 * Browser microphone recorder using MediaRecorder.
 * start() prompts for mic permission (web contract) and begins recording.
 * stop() returns the recorded audio Blob (webm/ogg), which Whisper accepts.
 */
export function useWebRecorder() {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const mimeRef = useRef("audio/webm");

  const start = useCallback(async () => {
    setPermissionBlocked(false);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setPermissionBlocked(true);
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
      mimeRef.current = mime || "audio/webm";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return true;
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        setPermissionBlocked(true);
      }
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const mr = mediaRef.current;
      if (timerRef.current) clearInterval(timerRef.current);
      if (!mr) {
        setRecording(false);
        resolve(null);
        return;
      }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setRecording(false);
        resolve(blob.size > 0 ? blob : null);
      };
      try {
        mr.stop();
      } catch {
        setRecording(false);
        resolve(null);
      }
    });
  }, []);

  return { recording, elapsed, permissionBlocked, start, stop };
}
